import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_MY_CONVERSATIONS } from '../../graphql/operations';
import './ForwardMessageModal.css';

/**
 * KAP-311.1 — picker + optional note for forwarding a message to another
 * conversation. The picker lists every conversation the caller is a member
 * of (channels, 1:1 DMs, group DMs) except the source. Selection + note
 * → calls `onSubmit(targetConversationId, note)`. Parent owns the
 * `forwardMessage` mutation and the "Forwarded" toast.
 *
 * Esc + Cancel close the modal and return focus to `returnFocusRef` (the
 * kebab trigger), mirroring the LinkPopover focus-return pattern called
 * out in PR #100. Backdrop click also closes.
 */
const NOTE_MAX = 4000;

function ForwardMessageModal({
  sourceMessage,
  onClose,
  onSubmit,
  returnFocusRef
}) {
  const [targetConversationId, setTargetConversationId] = useState(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const firstFocusRef = useRef(null);
  const dialogRef = useRef(null);

  const { data, loading } = useQuery(LIST_MY_CONVERSATIONS, { fetchPolicy: 'cache-and-network' });

  const conversations = useMemo(() => {
    const items = data?.listMyConversations || [];
    return items
      .map((row) => row?.conversation)
      .filter(Boolean)
      .filter((c) => c.id !== sourceMessage?.conversationId);
  }, [data, sourceMessage]);

  const grouped = useMemo(() => {
    const channels = [];
    const dms = [];
    const groupDms = [];
    for (const c of conversations) {
      const t = String(c.type || '').toUpperCase();
      if (t === 'CHANNEL') channels.push(c);
      else if (t === 'GROUP_DM') groupDms.push(c);
      else if (t === 'DM' || t === 'DIRECT' || t === 'DIRECT_MESSAGE') dms.push(c);
      else channels.push(c);
    }
    channels.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    dms.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    groupDms.sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
    return { channels, groupDms, dms };
  }, [conversations]);

  useEffect(() => {
    if (firstFocusRef.current && typeof firstFocusRef.current.focus === 'function') {
      firstFocusRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleClose() {
    if (returnFocusRef?.current && typeof returnFocusRef.current.focus === 'function') {
      try {
        returnFocusRef.current.focus();
      } catch (_err) {
        // Focus restoration failed (element unmounted) — close anyway.
      }
    }
    onClose();
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!targetConversationId || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      await onSubmit({
        sourceMessage,
        targetConversationId,
        note: note.trim()
      });
      handleClose();
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to forward message.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="forward-message-modal-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        ref={dialogRef}
        className="forward-message-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="forward-message-modal-title"
      >
        <header className="forward-message-modal-header">
          <h2 id="forward-message-modal-title">Forward message</h2>
          <button
            type="button"
            className="forward-message-modal-close"
            aria-label="Close"
            onClick={handleClose}
          >
            ×
          </button>
        </header>

        <form className="forward-message-modal-body" onSubmit={handleSubmit}>
          <fieldset className="forward-message-modal-target">
            <legend>Forward to</legend>
            {loading && !conversations.length ? (
              <p className="forward-message-modal-loading">Loading conversations…</p>
            ) : (
              <div
                className="forward-message-modal-target-list"
                role="radiogroup"
                aria-label="Forward target conversation"
              >
                {grouped.channels.length > 0 && (
                  <ConversationGroup
                    label="Channels"
                    conversations={grouped.channels}
                    selectedId={targetConversationId}
                    onSelect={setTargetConversationId}
                    firstFocusRef={firstFocusRef}
                  />
                )}
                {grouped.groupDms.length > 0 && (
                  <ConversationGroup
                    label="Group DMs"
                    conversations={grouped.groupDms}
                    selectedId={targetConversationId}
                    onSelect={setTargetConversationId}
                  />
                )}
                {grouped.dms.length > 0 && (
                  <ConversationGroup
                    label="Direct messages"
                    conversations={grouped.dms}
                    selectedId={targetConversationId}
                    onSelect={setTargetConversationId}
                  />
                )}
                {!loading && conversations.length === 0 && (
                  <p className="forward-message-modal-empty">No other conversations to forward to.</p>
                )}
              </div>
            )}
          </fieldset>

          <label className="forward-message-modal-note-label" htmlFor="forward-note">
            Add a note (optional)
          </label>
          <textarea
            id="forward-note"
            className="forward-message-modal-note"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX))}
            rows={3}
            maxLength={NOTE_MAX}
            placeholder="Add a comment…"
          />

          {errorMessage && (
            <p className="forward-message-modal-error" role="alert">{errorMessage}</p>
          )}

          <div className="forward-message-modal-actions">
            <button
              type="button"
              className="forward-message-modal-btn"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="forward-message-modal-btn forward-message-modal-btn-primary"
              disabled={!targetConversationId || submitting}
            >
              {submitting ? 'Forwarding…' : 'Forward'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConversationGroup({ label, conversations, selectedId, onSelect, firstFocusRef }) {
  return (
    <div className="forward-message-modal-target-group">
      <span className="forward-message-modal-target-group-label">{label}</span>
      {conversations.map((c, idx) => {
        const id = c.id;
        const title = c.viewerTitle || c.title || c.id;
        const isFirst = firstFocusRef && idx === 0;
        return (
          <label key={id} className="forward-message-modal-target-row">
            <input
              ref={isFirst ? firstFocusRef : undefined}
              type="radio"
              name="forward-target"
              value={id}
              checked={selectedId === id}
              onChange={() => onSelect(id)}
            />
            <span>{title}</span>
          </label>
        );
      })}
    </div>
  );
}

export default ForwardMessageModal;
